<template>
  <le-bubble theme="dark" trigger="manual" :open="showError" :placement="errorPlacement" :hideOnTargetClick="true">
    <template slot="target">
      <div
        :class="['le-tags', { 'active': searchInputFocused }, { 'disabled': !enabled }]"
        :style="tagsStyle"
        tabindex="-1"
        ref="tagsContainer"
        @focus="onOuterFocus">
        <le-tag
          v-for="tag in tags"
          :key="tag[tagIdAttr]"
          :idAttr="tagIdAttr"
          :valueAttr="tagValueAttr"
          :subtextAttr="tagSubtextAttr"
          :tag="tag"
          :enabled="enabled"
          :clickable="clickableTags"
          @unselect="onUnselect(tag[tagIdAttr])"
          @select="onSelect(tag[tagIdAttr])">
        </le-tag>
        <div
          :class="['search-container', { 'disabled': !enabled }]"
          :style="inputContainerStyle">
          <div
            :class="['search-input', { 'has-error': hasError }]"
            ref="searchInput"
            aria-autocomplete="list"
            :contenteditable="enabled"
            :tabindex="tabIndex"
            :placeholder="placeholderOnNoSelection"
            :aria-controls="controlId"
            @keyup="onKeyup"
            @cut="onCut"
            @paste="onPaste"
            @keydown.enter.prevent="onSubmit"
            @focus.prevent="emitFocusEvent"
            @blur="emitBlurEvent"
            @keydown.tab="onTab">{{ searchInputText }}</div>
        </div>
      </div>
    </template>
    <template slot="content">
      {{ bubbleErrorMsg }}
    </template>
  </le-bubble>
</template>


<script>
  import { debounce, find, get } from 'lodash';
  import leTag from './le-tag';
  import leBubble from '../le-bubble/le-bubble';

  const specialSpace = String.fromCharCode(160);
  const regularSpace = String.fromCharCode(32);

  export default {
    name: 'le-tags',
    components: {
      leTag,
      leBubble,
    },
    props: {
      searchTerm: {
        type: String,
        default: '',
      },
      tags: {
        type: Array,
        required: true,
      },
      placeholder: {
        type: String,
        default: 'Select <value>',
      },
      tabbable: {
        type: Boolean,
        default: true,
      },
      tagIdAttr: {
        type: String,
        default: 'id',
      },
      tagValueAttr: {
        type: String,
        default: 'value',
      },
      tagSubtextAttr: {
        type: String,
        default: 'subtext',
      },
      errorPlacement: {
        type: String,
        default: 'bottom',
      },
      enabled: {
        type: Boolean,
        default: true,
      },
      maxHeight: {
        type: String,
      },
      showVerticalScrollbar: {
        type: Boolean,
      },
      clearInputOnTagAdded: {
        type: Boolean,
        default: true,
      },
      clickableTags: {
        type: Boolean,
        default: false,
      },
      controlId: {
        type: String,
      },
    },
    data() {
      return {
        searchInputText: this.searchTerm || '',
        searchInputFocused: false,
        errorMsg: '',
        bubbleErrorMsg: '',
        showError: false,
        searchInputHasValue: !!this.searchTerm,
      };
    },
    computed: {
      placeholderOnNoSelection() {
        return this.tags.length === 0 ? this.placeholder : '';
      },
      inputContainerStyle() {
        return this.placeholderOnNoSelection || this.searchTerm || this.searchInputHasValue ? { width: 'auto' } : { width: 0 };
      },
      hasError() {
        return this.errorMsg.length > 0;
      },
      tagsStyle() {
        const style = {};
        if (this.maxHeight) {
          style['max-height'] = this.maxHeight;
        }
        if (this.showVerticalScrollbar) {
          style['overflow-y'] = this.showVerticalScrollbar ? 'auto' : 'visible';
        }
        return style;
      },
      tabIndex() {
        return this.tabbable ? '0' : '-1';
      },
    },
    methods: {
      onUnselect(id) {
        const removedTag = find(this.tags, { id });
        this.$emit('unselect', removedTag);
        this.$refs.searchInput.focus();
      },
      onSelect(id) {
        const selectedTag = find(this.tags, { id });
        this.$emit('select', selectedTag);
      },
      scrollToBottom() {
        this.$refs.tagsContainer.scrollTop = this.$refs.tagsContainer.scrollHeight;
      },
      focus() {
        if (this.enabled) {
          this.$refs.searchInput.focus();
        }
      },
      blur() {
        this.$refs.searchInput.blur();
        this.searchInputFocused = false;
      },
      // eslint-disable-next-line
      onKeyup: debounce(function () {
        this.onTagInputValueChange();
      }, 150),
      onSubmit() {
        this.$emit('searchInputSubmit');
      },
      onCut() {
        this.onTagInputValueChange();
      },
      onPaste() {
        this.onTagInputValueChange();
      },
      onTagInputValueChange() {
        if (this.enabled) {
          const text = get(this.$refs, 'searchInput.innerText', '');
          const textWithFixedSpaces = text.replace(specialSpace, regularSpace);
          this.$emit('input', textWithFixedSpaces);
          this.searchInputHasValue = !!this.$refs.searchInput.innerText;
          this.errorMsg = '';
        }
      },
      onOuterFocus() {
        if (this.enabled) {
          this.$refs.searchInput.focus();
        }
      },
      emitFocusEvent() {
        if (this.enabled) {
          this.searchInputFocused = true;
          this.$emit('focus');
          this.clearError();
        }
      },
      emitBlurEvent() {
        this.searchInputFocused = false;
        this.$emit('blur');
      },
      setError(showError, errorMsg) {
        if (showError) {
          this.errorMsg = errorMsg;
          this.bubbleErrorMsg = this.errorMsg;
        } else {
          this.clearError();
        }
        this.toggleErrorBubble(this.hasError && this.searchInputFocused);
      },
      clearError() {
        this.toggleErrorBubble(false);
        this.errorMsg = '';
      },
      toggleErrorBubble(showBubble) {
        this.showError = showBubble;
      },
      onTab() {
        this.$emit('tabout');
      },
    },
    watch: {
      tags(newTags, oldTags) { // If we added new tags, we want to clear the input
        if (this.clearInputOnTagAdded && (newTags.length > oldTags.length)) {
          this.$refs.searchInput.innerText = '';
        }
      },
      searchTerm() {
        if (this.searchTerm === '') {
          this.$refs.searchInput.innerText = '';
          this.searchInputHasValue = false;
        }
      },
      tabbable() {
        if (this.tabbable) {
          this.$refs.searchInput.focus();
        }
      },
    },
  };
</script>

<style lang="scss">
  .le-tags {
    display: flex;
    flex-wrap: wrap;
    outline: none;
    min-height: 26px;
    max-height: 135px;
    overflow-x: hidden;
    border-radius: 1px;
    padding: 4px 5px 0 8px;
    font-family: Roboto;
    @include themify {
      background-color: getColor('tagsContainerBackground');
      border: 1px solid getColor('tagsContainerBorder');
    }
    &.active {
      @include themify {
        border-color: getColor('tagsContainerBorderFocus');
      }
    }
    &.disabled {
      @include themify {
        border-color: getColor('tagsBorderDisabled');
        background-color: getColor('tagsBackgroundDisabled');
      }
    }

    .search-container {
      display: inline-block;
      margin: 1px 6px 5px 0;
      height: 18px;
      .search-input {
        line-height: 18px;
        border-radius: 1px;
        outline: none;
        font-size: 12px;
        font-weight: 500;
        word-break: break-all;
        margin-left: 2px;
        padding: 0 5px;
        @include themify {
          border: 1px dashed getColor('tagBorder');
          color: getColor('tagsSearchInput');
        }
        &:empty {
          border: none;
          height: 20px;
          line-height: 20px;
        }
        &.has-error {
          @include themify {
            border-color: getColor('error');
          }
        }
      }
      @include themify {
        :not(:focus):empty::before {
          content: attr(placeholder);
          display: block; /* For Firefox */
          font-size: 12px;
          font-style: italic;
          color: getColor('tagsPlaceholder');
          border: 0;
        }
        &.disabled {
          :focus:empty::before,
          :not(:focus):empty::before{
            content: attr(placeholder);
            font-style: italic;
            color: getColor('tagsPlaceholderDisabled');
          }
        }
        padding: 0;
      }
    }
  }
</style>
